import React from 'react';
import { Player, TacticalPosition } from '../../domain/entities';
import { Modal } from './Modal';
import './SkillDetailModal.css';
import { calculateMatchPerformance, calculateRoleAndFootModifier } from '../../utils/utilities';

interface MatchPerformanceDetailModalProps {
    player: Player | null;
    isOpen: boolean;
    onClose: () => void;
    tacticalPosition: TacticalPosition;
}

export const MatchPerformanceDetailModal: React.FC<MatchPerformanceDetailModalProps> = ({ player, isOpen, onClose, tacticalPosition }) => {
    if (!player) return null;

    const formClass = player.form > 0 ? 'positive' : player.form < 0 ? 'negative' : 'text-secondary';
    
    const baseEffectiveSkill = player.skill + player.form;
    const fitnessModifier = 0.5 + (player.fitness / 200);
    const injuryModifier = player.injury?.type === 'Light' ? 0.8 : 1.0;
    const baseMatchPerformance = baseEffectiveSkill * fitnessModifier * injuryModifier;
    const roleAndFootModifier = tacticalPosition !== 'RES' ? calculateRoleAndFootModifier(player, tacticalPosition) : 0;
    const finalPerformance = baseMatchPerformance * (1 - roleAndFootModifier);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <h2>Match Performance: {player.name}</h2>
            <div className="skill-detail-modal-content">
                <p><strong>Expected Match Performance</strong> is an estimate of a player's ability in their next match, for the selected position (<strong>{tacticalPosition}</strong>).</p>
                
                <ul className="skill-breakdown-list">
                    <li>
                        <span>Base Skill</span>
                        <span>{player.skill}</span>
                    </li>
                    <li>
                        <span>Form Modifier</span>
                        <span className={formClass}>{player.form > 0 ? '+' : ''}{player.form}</span>
                    </li>
                    <li className="total">
                        <span>Effective Skill</span>
                        <span>{baseEffectiveSkill}</span>
                    </li>
                </ul>
                <ul className="skill-breakdown-list" style={{marginTop: 0, borderTop: 'none'}}>
                     <li>
                        <span>Fitness ({player.fitness}%) Modifier</span>
                        <span>x{fitnessModifier.toFixed(2)}</span>
                    </li>
                     <li>
                        <span>Injury Modifier</span>
                        <span>x{injuryModifier.toFixed(2)}</span>
                    </li>
                    <li className="total">
                        <span>Base Match Performance</span>
                        <span>~{Math.round(baseMatchPerformance)}</span>
                    </li>
                </ul>

                {tacticalPosition !== 'RES' && roleAndFootModifier > 0 && (
                    <>
                        <h3>Role & Foot Modifier</h3>
                        <p>Penalty applied if player's role preference or favored foot doesn't match the tactical position.</p>
                        <ul className="skill-breakdown-list">
                            <li>
                                <span>Role/Foot Suitability Penalty</span>
                                <span className={roleAndFootModifier > 0 ? 'negative' : 'positive'}>-{Math.round(roleAndFootModifier * 100)}%</span>
                            </li>
                            <li className="total" style={{fontSize: '1.2em'}}>
                                <span>Final Match Performance for {tacticalPosition}</span>
                                <span>~{Math.round(finalPerformance)}</span>
                            </li>
                        </ul>
                    </>
                )}

                {tacticalPosition !== 'RES' && roleAndFootModifier === 0 && (
                    <ul className="skill-breakdown-list">
                        <li className="total" style={{fontSize: '1.2em'}}>
                            <span>Final Match Performance for {tacticalPosition}</span>
                            <span>~{Math.round(finalPerformance)}</span>
                        </li>
                    </ul>
                )}

            </div>
            <div className="modal-actions">
                <button className="button" onClick={onClose}>Close</button>
            </div>
        </Modal>
    );
};